'use client'

import type { PeripheralBalanceType } from '@morpho-org/blue-sdk'

import { useSet } from '@morpho-org/dapp-core'
import { useCallback, useEffect, useState } from 'react'

interface UseEditableAssetInputProps {
  value: bigint | null
  maxValue: bigint | undefined
  isMax: boolean
  onMaxClick: (value: boolean) => void
  onValueChange: (value: bigint | null) => void
  peripheralOptionsState: Set<PeripheralBalanceType>
  onPeripheralTokensOptionsStateChange: (value: Set<PeripheralBalanceType>) => void
}

/**
 * Custom hook `useEditableAssetInput` to manage the state and logic for an editable asset input component.
 */
export const useEditableAssetInput = ({
  value,
  maxValue,
  isMax,
  peripheralOptionsState,
  onMaxClick,
  onValueChange,
  onPeripheralTokensOptionsStateChange
}: UseEditableAssetInputProps) => {
  const [isEditing, setIsEditing] = useState(false)

  const [localValue, setLocalValue] = useState<bigint | null>(value)
  const [localIsMax, setLocalIsMax] = useState<boolean>(isMax)

  // If the local max is set to true we force the local value to be the max value.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (localIsMax && maxValue !== undefined) setLocalValue(maxValue)
  }, [localIsMax, maxValue])

  const [
    localPeripheralOptionsState,
    ,
    ,
    toggleLocalPeripheralOptionState,
    setLocalPeripheralOptionState
  ] = useSet(peripheralOptionsState)

  // Confirmation and Cancel Actions
  const onEditConfirm = useCallback(() => {
    onMaxClick(localIsMax)
    onValueChange(localValue)
    onPeripheralTokensOptionsStateChange(localPeripheralOptionsState)
    setIsEditing(false)
  }, [
    onMaxClick,
    localIsMax,
    onValueChange,
    localValue,
    onPeripheralTokensOptionsStateChange,
    localPeripheralOptionsState
  ])

  const onEditCancel = useCallback(() => {
    setIsEditing(false)
  }, [])

  // If the input is not being edited, the state is synced.
  useEffect(() => {
    if (isEditing) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocalIsMax(isMax)
    setLocalValue(value)
    setLocalPeripheralOptionState(peripheralOptionsState)
  }, [isEditing, isMax, value, peripheralOptionsState, setLocalPeripheralOptionState])

  return {
    localValue,
    localIsMax,
    localPeripheralOptionsState,
    isEditing,
    setLocalValue,
    setIsEditing,
    onEditConfirm,
    onEditCancel,
    setLocalIsMax,
    onLocalPeripheralOptionStateChange: toggleLocalPeripheralOptionState
  }
}
